import { AbstractAssetTask, AssetsManager, ContainerAssetTask, MeshAssetTask } from '@babylonjs/core';
import { Asset, AssetType } from './Asset';
import { AssetObject } from './AssetObject';
import manifest from './assets/manifest.json';
import { Game } from './Game';
import { GameConfig } from './GameConfig';

export interface ManifestEntry {
  name: string;
  type: 'file' | 'directory';
  path: string;
  extension?: string;
  children?: ManifestEntry[];
}

export interface AssetDefinitionQueryResult {
  assets: Asset[];
  assetObjects: AssetObject[];
}

export class AssetFactory {
  assetsManager: AssetsManager;
  assets: Asset[] = [];

  constructor(public game: Game) {
    this.assetsManager = new AssetsManager(this.game.scene);
    this.assetsManager.autoHideLoadingUI = false;
  }

  getAsset(name: string): Asset {
    return this.assets.find((asset) => asset.name === name);
  }

  getAssets(name: string): Asset[] {
    return this.assets.filter((asset) => asset.name.includes(name));
  }

  isLoaded(name: string): boolean {
    return this.getAsset(name) !== undefined && this.getAsset(name).task !== undefined;
  }

  async load(assets: Asset[]): Promise<Asset[]> {
    return new Promise((resolve, reject) => {
      for (let asset of assets) {
        if (!this.isLoaded(asset.name)) {
          if (asset.type === AssetType.CONTAINER) {
            asset.task = this.assetsManager.addContainerTask(asset.name, asset.meshNames, asset.rootUrl, asset.fileName);
          } else if (asset.type === AssetType.MESH) {
            asset.task = this.assetsManager.addMeshTask(asset.name, asset.meshNames, asset.rootUrl, asset.fileName);
          } else if (asset.type === AssetType.IMAGE) {
            asset.task = this.assetsManager.addImageTask(asset.name, asset.rootUrl + asset.fileName);
          } else if (asset.type === AssetType.TEXT) {
            asset.task = this.assetsManager.addTextFileTask(asset.name, asset.rootUrl + asset.fileName);
          } else if (asset.type === AssetType.BINARY) {
            asset.task = this.assetsManager.addBinaryFileTask(asset.name, asset.rootUrl + asset.fileName);
          }

          this.assets.push(asset);
        }
      }

      this.assetsManager.onProgress = (remainingCount: number, totalCount: number, task: AbstractAssetTask) => {
        this.game.loadingScreen.setProgress('Asset: ' + task.name, {
          lengthComputable: true,
          loaded: totalCount - remainingCount,
          total: totalCount,
        });
      };

      this.assetsManager.onFinish = (assetTasks: AbstractAssetTask[]) => {
        resolve(assets);
      };

      this.assetsManager.onTaskSuccess = (task: AbstractAssetTask) => {
        if (GameConfig.logDebug) {
          console.log('Asset loaded successfully', task);
          if (task instanceof MeshAssetTask || task instanceof ContainerAssetTask) {
            console.log(
              'Asset Task',
              task.name,
              'contains following meshes: ',
              task.loadedMeshes.map((m) => {
                return m.name;
              })
            );
          }
        }
        // Hide all loaded meshes by default - only clones should be visible
        // This prevents original asset meshes from appearing at 0,0,0
        if (task instanceof MeshAssetTask || task instanceof ContainerAssetTask) {
          for (const mesh of task.loadedMeshes) {
            mesh.isVisible = false;
            mesh.setEnabled(false);
          }
        }
      };

      this.assetsManager.onTaskError = (task: AbstractAssetTask) => {
        console.error('Error loading asset', task);
        reject(task);
      };

      this.assetsManager.load();
    });
  }

  static queryAssetDefinitions(...queries: string[]): AssetDefinitionQueryResult {
    const foundEntries: ManifestEntry[] = [];

    for (const query of queries) {
      AssetFactory.findManifestFiles('.glb', manifest as ManifestEntry, query, foundEntries);
    }

    const assets: Asset[] = [];
    const assetObjects: AssetObject[] = [];

    for (const entry of foundEntries) {
      // Extract asset name (filename without extension)
      const assetName = entry.name.replace('.glb', '');

      // Extract rootUrl from path (directory path with trailing slash)
      const pathParts = entry.path.split('/');
      pathParts.pop(); // Remove filename
      const rootUrl = pathParts.length > 0 ? pathParts.join('/') + '/' : '';

      const asset = new Asset(assetName, AssetType.MESH, 'assets/' + rootUrl, entry.name);
      assets.push(asset);

      const assetObject = AssetObject.fromAsset(assetName).withClone(true);
      //const assetObject = new AssetObject(assetName, assetName, 1, true, true);
      assetObjects.push(assetObject);
    }
    if (GameConfig.logDebug) {
      console.log('Found assets for queries', queries, assets);
      console.log('Found asset objects for queries', queries, assetObjects);
    }
    return { assets, assetObjects };
  }

  static getManifestFiles(path: string, extension?: string | string[]): ManifestEntry[] {
    const files: ManifestEntry[] = [];
    AssetFactory.findManifestFiles(extension, manifest as ManifestEntry, path, files);
    return files;
  }

  static getManifestFileExtension(path: string): string {
    const files: ManifestEntry[] = AssetFactory.getManifestFiles(path);
    if (files[0]) {
      return files[0].extension;
    }
    throw new Error('Could not find file at path: ' + path);
  }

  static findManifestFiles(extension: string | string[], entry: ManifestEntry, query: string, results: ManifestEntry[]): void {
    if (!extension) {
      extension = [];
    }
    const extensions = Array.isArray(extension) ? extension : [extension];
    if (entry.type === 'file' && (extensions.length == 0 || extensions.includes(entry.extension))) {
      if (entry.path.toLowerCase().includes(query.toLowerCase())) {
        results.push(entry);
      }
    }
    if (entry.children) {
      for (const child of entry.children) {
        AssetFactory.findManifestFiles(extension, child, query, results);
      }
    }
  }
}
